import { useEffect, useRef, useState, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box, Checkbox, FormControlLabel } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, replace, size, cloneDeep, isNull, isUndefined, isEqual } from "lodash";

import bfTrainingAdjService from "@plannel/services/data/bf-training-adj-service";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import {DPItemAutocomplete} from "@plannel/components/filter/ItemAutocomplete";
import CustomerAutocomplete from "@plannel/components/filter/CustomerAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "../../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import CodeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import SplitterContainer from "@plannel/components/SplitterContainer";
import {lightBlue} from "@mui/material/colors";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import DateUtils from "@plannel/utils/date-util";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";
import planConfigService from "@plannel/services/data/plan-config-service";
import { FS } from "@plannel/utils/font-util";

const CURRENT_DATE = new ZDate();
const START_MIN_DATE = new ZDate(CURRENT_DATE.getFullYear() - 10, 0, 1);
const START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth(), 1);
const MAX_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const DEFAULT_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 0);
const initPeriod = { start: START_DATE, end: DEFAULT_END_DATE }

const { snackBarMessageColor } = ColorUtil;

const BfTrainingAdjustment = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxNotifyState = reduxUtil.getViewState(`${viewName}_NOTIFY`);
  const reduxDispatch = useDispatch();
  const showLimitSnackbar = useRef(reduxNotifyState ? reduxNotifyState?.showLimitSnackbar : true);
  const userData = JSON.parse(localStorage.getItem("user"));

  const { dpConfig, module: { DP }, buckets, dayOfWeek } = CodeUtil;
  const pageSizes = [100, 200, 500];
  const [resultTotalCount, setResultTotalCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [sortParams, setSortParams] = useState({});
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const customerSearch = useRef(reduxViewState?.customerSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const extraCustomerCodesRef = useRef(reduxViewState?.extraCustomerCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");
  const [npiFlg, setNpiFlg] = useState(reduxViewState?.npiFlg ?? false);
  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : initPeriod);
  const [maxEndDate, setMaxEndDate] = useState(MAX_END_DATE);
  const [limitInfo, setLimitInfo] = useState({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const chartRawRef = useRef(null); // 마지막 차트 입력 — 언어 변경 시 재조립용
  const [gridLoading, setGridLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);
  const [bfTrainingAdjs, setBfTrainingAdjs] = useState([]);
  const [csvColDef, setCsvColDef] = useState([]);
  const [header, setHeader] = useState([]);
  const [bucket, setBucket] = useState(reduxViewState?.bucket ?? buckets.MONTH);
  const [startDay, setStartDay] = useState(reduxViewState?.startDay ?? dayOfWeek.MONDAY.cd);
  const currencyMap = useRef({});
  const EXCEL_EXPORT = "excelExport";

  const defaultTypeOptions = { strokeWidth: 1.5, barSize: 20 };
  const chartSeries = [
    { field: "salesActualQty", text: t("salesActualQty"), type: "line", typeOptions: { ...defaultTypeOptions}, orientation: "left", groupName: t("qty") },
    { field: "lastYearSalesQty", text: t("lastYearSalesQty"), type: "line", typeOptions: { ...defaultTypeOptions }, orientation: "left", groupName: t("qty") }
    ]
  const pivotConfig = {
    groupKey: "measure",
    chartSeries,
    include: chartLabel
  };
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (extraCustomerCodesRef.current?.length > 0) {
      searchFilters.customers = [customerSearch.current, ...extraCustomerCodesRef.current].filter(Boolean);
    } else if (customerSearch.current) {
      searchFilters.customer = customerSearch.current;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    } 

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }
    searchFilters.npiFlg = npiFlg;

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  };

  const retrievePlanConfigs = () => {
    const param = {
      moduleCd: DP,
      activeFlg: true
    };
    planConfigService.getAll(param)
      .then(response => {
        if (response.data?.results) {
          const resultData = response.data.results;
          for (const result of resultData) {
            if (isEqual(result.configCd, dpConfig.BUCKET_UNIT)) {
              setBucket(result.strVal);
            } else if (isEqual(result.configCd, dpConfig.START_DAY)) {
              setStartDay(result.strVal);
            }
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveBfTrainingAdj= () => {
    const params = getRequestParams();
    bfTrainingAdjService.getAll(params)
      .then(response => {
        const { results, totalPages, totalItems, isLimit, limitSize, currency, chartData, header } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setLimitInfo({ isLimit, limitSize });
        }
        setHeader(header);
        setBfTrainingAdjs(results);

        setTotalPages(totalPages);
        setResultTotalCount(totalItems);
        if (size(currency)) {
          currencyMap.current = currency;
        }
        if (chartData) {
          const row = chartData[0];
          makeChartData(chartData, results, header, row?.customerId, row?.itemId);
          setChartLabel(header);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, customerSearch: customerSearch.current, extraCustomerCodes: extraCustomerCodesRef.current, currentPage, pageSize, ...period, advancedFilters, npiFlg, bucket, startDay });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  };

  const makeChartData = (data, adjGridData, headers, customerId, itemId) => {
    chartRawRef.current = { data, adjGridData, headers, customerId, itemId };

    if (isEmpty(data) || isEmpty(headers)) {
      setChartData([]);
    }
    const dataMap = new Map(
      data.map(item => [item.txnDate, item])
    );
    const lastObj = { measure: t("lastYearSalesQty")};
    const salesObj = { measure: t("salesActualQty")};
    const filteredData = adjGridData.filter(item => item.itemId === itemId && item.customerId === customerId);
    const gridMap = new Map(
      filteredData.map(item => [item.txnDate, item])
    );
    for (const header of headers) {
      if (dataMap.get(header) != null) {
        const item = dataMap.get(header);
        const yoy = item?.yoyQty;
        const gridData = gridMap.get(header);
        const sales = gridData != null && !isUndefined(gridData?.salesQtyAdj) && !isNull(gridData?.salesQtyAdj) ? gridData.salesQtyAdj : item?.salesQty;
        lastObj[header] = yoy ?? 0;
        salesObj[header] = sales ?? 0;
      } else {
        lastObj[header] = 0;
        salesObj[header] = 0;
      }
    }
    setChartData([{...lastObj}, {...salesObj}]);
  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    const raw = chartRawRef.current;
    if (isNull(raw) || isUndefined(raw)) {
      return;
    }
    makeChartData(raw.data, raw.adjGridData, raw.headers, raw.customerId, raw.itemId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const retrieveBfTrainingAdjChart= (customerId, itemId) => {
    if (customerId == null || itemId == null) {
      setChartData([]);
    }
    const params = {
      customerId,
      itemId,
      startDate: period?.start,
      endDate: period?.end
    }
    bfTrainingAdjService.getChart(params)
      .then(response => {
        if (response?.data) {
          makeChartData(response?.data?.chartData, bfTrainingAdjs, header, customerId, itemId)
        } else {
          setChartData([]);
        }
      })
      .catch(() => {
        setChartData([]);
      })
    setChartLabel(header);
  };

  const refreshGrid = () => {
    retrieveBfTrainingAdj();
  };

  const editableCellStyle = params => {
    const cellStyle = {};
    const editableColumns = ["descTxt", "salesQtyAdj"];
    if (editableColumns.includes(params.colDef.field)) {
      cellStyle.background = lightBlue[100];
    }
    return cellStyle;
  }

  const currencyRenderer = (param, isExport = false) => {
    const customerCd = param?.data?.customerCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[customerCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      }
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT
  const onCellValueChanged = (params) => {
    const { column, value } = params;
    const colId = column.getColId();
    if (isEqual(colId, "salesQtyAdj")) {
      for (const item of chartData) {
        if (item.measure === t("salesActualQty")) {
          item[params.data.txnDate] = value;
          break;
        }
      }
      setChartData([...chartData]);
    }
    DataState.setDataState(params);
  }

  const onCellGridClicked = e => {
    const customerId = e.data.customerId;
    const itemId = e.data.itemId;
    retrieveBfTrainingAdjChart(customerId, itemId);
  };

  const onChange = (e, type) => {
    if (type) {
      if (type === "start") {
        e.setHours(0);
        e.setMinutes(0);
        e.setSeconds(0);
        if (!CodeUtil.isWeekBucket(bucket)) {
          e.setDate(1);
        }
      } else if (type === "end") {
        e.setHours(23);
        e.setMinutes(59);
        e.setSeconds(59);
        if (!CodeUtil.isWeekBucket(bucket)) {
          const lastDay = new Date(e.getFullYear(), e.getMonth() + 1, 0);
          e.setDate(lastDay.getDate());
        }
      }
      setPeriod({ ...period, [type]: e });
    }
  };

  const columnDefs = [
    { headerName: "itemCd", field: "itemCd", width: 150, cellClass: 'stringType', filterType: "string", type: ["nonEditableColumn"], },
    { headerName: "itemName", field: "itemNm", type: ["nonEditableColumn"], minWidth: 200, filterType: "string"},
    {
      headerName: "customerCd",
      field: "customerCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      cellClass: 'stringType',
      width: 180, filterType: "string"
    },
    {
      headerName: "customerName", field: "customerNm",
      type: ['nonEditableColumn'],
      minWidth: 200,
      filterType: "string",
    },
    { headerName: "txnDate", field: "txnDate", type: ["nonEditableColumn"], width: 150, filterType: "date" },
    { headerName: "salesQty", field: "salesQty", type: ["numberColumn", "rightAligned", "nonEditableColumn"], width: 120, filterType: "number" },
    { headerName: "salesAmt", field: "salesAmt", type: ["numberColumn", "rightAligned", "nonEditableColumn"], valueFormatter: currencyRenderer, width: 120, filterType: "number" },
    { headerName: "salesQtyAdj", field: "salesQtyAdj", type: ["numberColumn", "rightAligned"], cellEditor: "agNumberCellEditor", cellEditorParams: {min: 0, step: 1},
      width: 120, filterType: "number", cellStyle: editableCellStyle  },
    { headerName: "notes", field: "descTxt", cellClass: 'stringType', filterType: "string", cellStyle: editableCellStyle },
    { headerName: "newProduct", field: "newProduct", type: ["booleanColumn", "nonEditableColumn"], width: 100 },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: DateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const saveBfTraining = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      bfTrainingAdjService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleCheckbox = e => {
    const value = e.target.checked;
    setNpiFlg(value);
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveBfTraining();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveBfTrainingAdj();
    }
  }

  const onGridReady = (params) => {
    retrievePlanConfigs();

    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    setCsvColDef(cloneDeep(columnDefs))
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const setDateByTimeDimension = () => {
    let start = START_DATE;
    let end = DEFAULT_END_DATE;
    const isWeek = bucket === buckets.WEEK || bucket === buckets.PARTIAL_WEEK;
    if (isWeek) {
      start = DateUtils.nearDay(START_DATE, startDay, bucket);
      end = DateUtils.finishedNearDay(DEFAULT_END_DATE, startDay, bucket);
    }
    const bucketPeriod = {};
    bucketPeriod.start = start;
    bucketPeriod.end = end;
    let maxEndDate = new Date(MAX_END_DATE);
    if (bucket === buckets.WEEK) {
      maxEndDate = DateUtils.finishedNearDay(CURRENT_DATE, startDay, bucket);
    }
    setMaxEndDate(maxEndDate);
    setPeriod({ ...bucketPeriod });
  };

  useEffect(() => {
    if (isEmpty(csvColDef)) {
      return;
    }
    for (const col of csvColDef) {
      if (col.valueFormatter?.customType === EXCEL_EXPORT) {
        col.cellRenderer = null;
        col.valueFormatter = null;
        col.valueGetter = params => currencyRenderer(params, true);
        col.cellClass = params => {
          const value = Number(params.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvColDef])

  useEffect(() => {
    if (!reduxViewState?.bucket && bucket) {
      setDateByTimeDimension();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveBfTrainingAdj();
    }

    return () => {
      setBfTrainingAdjs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setBfTrainingAdjs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, period, npiFlg]);

  useEffect(() => {
    if (limitInfo?.isLimit && showLimitSnackbar.current) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.exportLimit, content: `${t("MSG_LimitedSearchResults", { N: limitInfo.limitSize })}` });
      reduxDispatch(reduxUtil.updateViewState({ viewName: `${viewName}_NOTIFY`, showLimitSnackbar: false }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitInfo?.isLimit, showLimitSnackbar.current]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);


  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <LocalizationProvider dateAdapter={AdapterDateFns} >
          <DatePicker
            label={t("periodStart")}
            views={CodeUtil.isWeekBucket(bucket) ? ["year", "month", "day"] : ["year", "month"]}
            format={CodeUtil.isWeekBucket(bucket) ? "yyyy-MM-dd" : "yyyy-MM"}
            value={period.start}
            openTo={"month"}
            maxDate={period.end ? period.end : MAX_END_DATE}
            minDate={START_MIN_DATE}
            shouldDisableMonth={(e) => WorkbenchUtil.ableMonthOfQuerter(e, bucket)}
            shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(e, bucket, startDay)}
            onAccept={(e) => onChange(e, "start")}
            slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1.5, width: 200 } } } }}
          />
          <DatePicker
            label={t("periodEnd")}
            views={CodeUtil.isWeekBucket(bucket) ? ["year", "month", "day"] : ["year", "month"]}
            format={CodeUtil.isWeekBucket(bucket) ? "yyyy-MM-dd" : "yyyy-MM"}
            value={period.end}
            openTo={"month"}
            maxDate={maxEndDate ?? MAX_END_DATE}
            minDate={period.start ? period.start : START_MIN_DATE}
            shouldDisableMonth={(e) => WorkbenchUtil.ableEndMonthOfQuerter(e, bucket)}
            shouldDisableDate={(e) => WorkbenchUtil.ableEndDayOfWeek(e, bucket, startDay)}
            onAccept={(e) => onChange(e, "end")}
            slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { mr: 1.5, width: 200 } } } }}
          />
        </LocalizationProvider>
        <CustomerAutocomplete retrieve={initPageToRetrieve} searchValue={customerSearch} sx={{ mr: 1 }} extraCodesRef={extraCustomerCodesRef} />
        <DPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} sx={{ mr: 1 }} extraCodesRef={extraItemCodesRef} />
        <FormControlLabel
          control={<Checkbox color="primary" name={"npiFlg"} checked={npiFlg} onChange={handleCheckbox}/>}
          label={t("newProduct")} key="npiFlg" sx={{ display: "flex" }} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={bfTrainingAdjService} columnDefs={csvColDef} totalRows={resultTotalCount}
                             params={getRequestParams()} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="sales-transaction-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ display: "flex", height: "100%" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <SplitterContainer initialSizes={[7, 4]}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={bfTrainingAdjs}
              {...defaultGridMemo}
              onGridReady={onGridReady}
              statusBar={statusBar}
              onCellValueChanged={onCellValueChanged}
              onCellClicked={onCellGridClicked}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
        </Box>
        <Box id="adj-chart" className="chart-grid-area chart-area" sx={{ height: "100%" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: FS.SUB }}}
              nullConvert
            />
          </Box>
        </SplitterContainer>
        </Box>
        {openFilter && (
          <>
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          </>
        )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        disableButton={dialogInfo.disableButton}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(BfTrainingAdjustment);